"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

import { MobileNavigation } from "~/components/MobileNavigation";
import { LanguageSwitcher } from "~/components/language-switcher";
import { Button } from "~/components/ui/button";
import type { SiteLocale } from "~/lib/graphql";

interface NavigationItem {
  slug: string;
  shortTitle: string;
}

export default function Header(props: {
  name: string;
  locale: SiteLocale;
  showSpaNavigation: boolean | null;
  navigationItems: Array<NavigationItem>;
  availableLocales?: SiteLocale[];
  currentSlug?: string;
  allSlugLocales?: Array<{
    locale: string;
    value: string;
  }>;
}) {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [activeHash, setActiveHash] = useState<string>("");
  const pathname = usePathname();

  useEffect(() => {
    const onHashChange = () => {
      setActiveHash(window.location.hash || "#top");
    };
    window.addEventListener("hashchange", onHashChange, false);

    // Set initial hash
    setActiveHash(window.location.hash || "#top");

    // --- ScrollSpy logic ---
    const handleScroll = () => {
      // Collect all anchor targets (including #top)
      const anchors = [
        { hash: "#top", offset: 0 },
        ...props.navigationItems.map((item) => {
          const el = document.getElementById(item.slug);
          return {
            hash: `#${item.slug}`,
            offset: el ? el.getBoundingClientRect().top + window.scrollY : Infinity,
          };
        }),
      ];
      // Find the last anchor above the current scroll position + some offset
      const scrollPosition = window.scrollY + 100; // 100px offset for header
      let current = "#top";
      for (const anchor of anchors) {
        if (scrollPosition >= anchor.offset) {
          current = anchor.hash;
        }
      }
      setActiveHash(current);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("hashchange", onHashChange, false);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [props.navigationItems]);

  return (
    <div className="fixed top-0 right-0 left-0 z-50 flex w-full items-center justify-center border-b border-[#E4E4E7] bg-white px-5 pt-4 pb-3 md:h-22">
      <header className="text-landing-800 relative z-40 container mx-auto flex max-w-[1440px] items-center justify-between px-0 font-bold">
        <Link href="/">{props.name}</Link>

        {/* landingpage has real subpages */}
        {!props.showSpaNavigation && (
          <div className="flex items-center gap-4">
            <nav className="hidden items-center space-x-6 md:flex">
              <Link
                href={`/${props.locale}`}
                className={`text-sm font-medium text-[#475569] transition-colors ${
                  pathname === `/${props.locale}` || pathname === "/"
                    ? "underline decoration-2 underline-offset-4"
                    : ""
                }`}
              >
                Home
              </Link>
              {props.navigationItems.map((item, index) => {
                const itemPath = `/${props.locale}/page/${item.slug}`;
                const isActive = pathname === itemPath;
                return (
                  <Link
                    key={item.slug + index}
                    href={itemPath}
                    className={`text-sm font-medium text-[#475569] transition-colors ${
                      isActive ? "underline decoration-2 underline-offset-4" : ""
                    }`}
                  >
                    {item.shortTitle}
                  </Link>
                );
              })}
            </nav>

            {/* Language Switcher */}
            {props.availableLocales && props.availableLocales.length > 1 && (
              <LanguageSwitcher
                currentLang={props.locale}
                availableLocales={props.availableLocales}
                currentSlug={props.currentSlug}
                allSlugLocales={props.allSlugLocales}
              />
            )}
          </div>
        )}

        {/* if showSpaNavigation show anchor navigation */}
        {props.showSpaNavigation && props.navigationItems.length > 0 ? (
          <div className="flex items-center gap-4">
            <nav className="hidden items-center space-x-6 md:flex">
              <Link
                href="#top"
                className={`text-sm font-medium text-[#475569] transition-colors ${
                  activeHash === "#top" ? "underline decoration-2 underline-offset-4" : ""
                }`}
              >
                Home
              </Link>
              {props.navigationItems.map((item, index) => (
                <Link
                  key={item.slug + index}
                  href={`#${item.slug}`}
                  className={`text-sm font-medium text-[#475569] transition-colors ${
                    activeHash === `#${item.slug}`
                      ? "underline decoration-2 underline-offset-4"
                      : ""
                  }`}
                >
                  {item.shortTitle}
                </Link>
              ))}
            </nav>

            {/* Language Switcher */}
            {props.availableLocales && props.availableLocales.length > 1 && (
              <LanguageSwitcher
                currentLang={props.locale}
                availableLocales={props.availableLocales}
                currentSlug={props.currentSlug}
                allSlugLocales={props.allSlugLocales}
              />
            )}
          </div>
        ) : null}

        {/* Mobile burger menu */}
        {props.showSpaNavigation && props.navigationItems.length > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="z-50 h-12 w-12 hover:bg-white/20 md:hidden [&_svg]:size-8"
            onClick={() => setIsMobileNavOpen(true)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              fill="none"
              viewBox="0 0 32 32"
              className="h-10 w-10"
            >
              <circle cx="16" cy="16" r="15.5" fill="#fff" stroke="#8C9091"></circle>
              <path
                stroke="#8C9091"
                strokeLinecap="round"
                strokeWidth="2"
                d="M9 9.581h14M9 16h14M9 22.419h14"
              ></path>
            </svg>
          </Button>
        )}

        {/* Mobile Navigation Modal */}
        <MobileNavigation
          navigationItems={props.navigationItems}
          isOpen={isMobileNavOpen}
          onClose={() => setIsMobileNavOpen(false)}
          locale={props.locale}
          availableLocales={props.availableLocales}
          currentSlug={props.currentSlug}
          allSlugLocales={props.allSlugLocales}
        />
      </header>
    </div>
  );
}
